import { Suspense, lazy, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import "../../styles/component-catalog.css";
import {
    COMPONENT_CATALOG_REGISTRY,
    COMPONENT_BASE_PATH,
    type ComponentCatalogRouteParams,
} from "./componentCatalogRegistry";

const ComponentCatalogDemoRenderer = lazy(
    () => import("./ComponentCatalogDemos")
);

export function ComponentCatalog() {
    const navigate = useNavigate();
    const { slug } = useParams<ComponentCatalogRouteParams>();

    const [searchText, setSearchText] = useState("");

    const sortedRegistry = useMemo(
        () =>
            [...COMPONENT_CATALOG_REGISTRY].sort((a, b) =>
                a.label.localeCompare(b.label)
            ),
        []
    );

    const activeEntry = slug
        ? sortedRegistry.find(
            (entry) => entry.slug === slug
        )
        : undefined;

    const searchResults = useMemo(() => {
        const query = searchText.trim().toLowerCase();

        if (!query) {
            return [];
        }

        return sortedRegistry.filter(
            (component) =>
                component.label.toLowerCase().includes(query)
        ).slice(0, 8);
    }, [searchText, sortedRegistry]);

    const handleComponentSelect = (selectedSlug: string): void => {
        navigate(`${COMPONENT_BASE_PATH}/${selectedSlug}`);
    };

    const handleSearchSelect = (selectedSlug: string): void => {
        setSearchText("");
        handleComponentSelect(selectedSlug);
    };

    return (
        <div className="component-catalog">
            <aside className="component-catalog__sidebar">
                <div className="component-catalog__sidebar-header">
                    <h2 className="component-catalog__title">
                        Component Catalog
                    </h2>

                    <div className="component-catalog__search">
                        <input
                            type="text"
                            className="component-catalog__search-input"
                            placeholder="Search components..."
                            value={searchText}
                            onChange={(event) =>
                                setSearchText(event.target.value)
                            }
                        />

                        {searchResults.length > 0 && (
                            <div className="component-catalog__search-dropdown">
                                {searchResults.map((component) => (
                                    <button
                                        key={component.slug}
                                        type="button"
                                        className="component-catalog__search-item"
                                        onClick={() => handleSearchSelect(component.slug)}
                                    >
                                        {component.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <nav
                    className="component-catalog__nav"
                    aria-label="Component catalog navigation"
                >
                    <ul className="component-catalog__list">
                        {sortedRegistry.map((entry) => {
                            const isActive = entry.slug === slug;

                            return (
                                <li
                                    key={entry.slug}
                                    className="component-catalog__list-item"
                                >
                                    <button
                                        type="button"
                                        className={
                                            isActive
                                                ? "component-catalog__nav-button component-catalog__nav-button--active"
                                                : "component-catalog__nav-button"
                                        }
                                        aria-current={
                                            isActive ? "page" : undefined
                                        }
                                        onClick={() =>
                                            handleComponentSelect(entry.slug)
                                        }
                                    >
                                        <span className="component-catalog__nav-label">
                                            {entry.label}
                                        </span>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </nav>
            </aside>

            <main className="component-catalog__content">
                {!slug ? (
                    <div className="component-catalog__welcome">
                        <h1 className="component-catalog__component-title">
                            Welcome to Component Catalog
                        </h1>

                        <p className="component-catalog__component-description">
                            Explore reusable UI components, patterns, and
                            interactive examples used throughout the
                            application. Select a component from the sidebar
                            or use search to quickly jump to a component demo.
                        </p>
                    </div>
                ) : activeEntry ? (
                    <>
                        <header className="component-catalog__content-header">
                            <h1 className="component-catalog__component-title">
                                {activeEntry.label}
                            </h1>

                            <p className="component-catalog__component-description">
                                {activeEntry.description}
                            </p>
                        </header>

                        <section className="component-catalog__preview">
                            <div className="component-catalog__preview-center">
                                <Suspense
                                    fallback={
                                        <div>
                                            Loading component demo...
                                        </div>
                                    }
                                >
                                    <ComponentCatalogDemoRenderer
                                        slug={activeEntry.slug}
                                    />
                                </Suspense>
                            </div>
                        </section>
                    </>
                ) : (
                    <div className="component-catalog__empty-state">
                        Component not found: <span className="component-catalog__renderer-missing">{slug}</span>
                    </div>
                )}
            </main>
        </div>
    );
}